import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";
import { formatDate } from "../utils/dateUtils";

const defaultTemplates = {
  "30_DAYS": "Reminder (1 Month Before): Hello {customerName}, your RTO Application {applicationNo} ({workTypeName}) appointment/follow-up is scheduled on {dueDate} (in 30 days). Please keep your required documents ready. Thank you! - MK MOTORS",
  "15_DAYS": "Reminder (15 Days Before): Hello {customerName}, your RTO Application {applicationNo} ({workTypeName}) appointment/follow-up is coming up on {dueDate} (in 15 days). Please confirm your availability. Thank you! - MK MOTORS",
  "5_DAYS": "Important Reminder (5 Days Before): Hello {customerName}, your RTO Application {applicationNo} ({workTypeName}) appointment/follow-up is in 5 days on {dueDate}. Please make sure all documents are ready. Thank you! - MK MOTORS",
  TODAY: "Reminder (Due Today): Hello {customerName}, your RTO Application {applicationNo} ({workTypeName}) appointment/follow-up is scheduled for TODAY ({dueDate}). Thank you! - MK MOTORS",
  OVERDUE: "Attention Required: Hello {customerName}, your RTO Application {applicationNo} ({workTypeName}) follow-up was scheduled on {dueDate} and requires immediate attention. Thank you! - MK MOTORS",
  ALL: "Reminder: Hello {customerName}, your RTO Application {applicationNo} ({workTypeName}) follow-up is scheduled for {dueDate}. Thank you! - MK MOTORS",
};

export default function WhatsAppRemindersPage() {
  const { http } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [activeInterval, setActiveInterval] = useState("30_DAYS"); // 30_DAYS | 15_DAYS | 5_DAYS | TODAY | OVERDUE | ALL
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    totalUpcoming: 0,
    count30Days: 0,
    count15Days: 0,
    count5Days: 0,
    countToday: 0,
    countOverdue: 0,
  });

  const [customTemplate, setCustomTemplate] = useState(defaultTemplates["30_DAYS"]);

  const loadReminders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get("/api/reminders/whatsapp/due", {
        params: { interval: activeInterval },
      });
      setItems(res.data.items || []);
      if (res.data.summary) {
        setSummary(res.data.summary);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load WhatsApp reminders.");
    } finally {
      setLoading(false);
    }
  }, [http, activeInterval]);

  useEffect(() => {
    loadReminders();
    setCustomTemplate(defaultTemplates[activeInterval] || defaultTemplates["30_DAYS"]);
  }, [loadReminders, activeInterval]);

  const insertVariable = (varTag) => {
    setCustomTemplate((prev) => `${prev} ${varTag}`);
  };

  const handleSendBatchReminders = async () => {
    if (!items.length) {
      setError("No customer reminder items available for sending in this interval.");
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await http.post("/api/reminders/whatsapp/send", {
        interval: activeInterval,
        customTemplate,
        enquiryIds: items.map((i) => i.id),
      });

      setSuccess(res.data.message);
      await loadReminders();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send WhatsApp reminders.");
    } finally {
      setSending(false);
    }
  };

  const handleSendSingleReminder = async (item) => {
    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await http.post("/api/reminders/whatsapp/send", {
        interval: activeInterval,
        customTemplate,
        enquiryIds: [item.id],
      });

      setSuccess(`Sent reminder to ${item.customerName} (${item.customerMobile}). ${res.data.message}`);
      await loadReminders();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send single WhatsApp reminder.");
    } finally {
      setSending(false);
    }
  };

  const openWhatsAppWeb = (item) => {
    const mobileClean = String(item.customerMobile || "").replace(/\D/g, "");
    const formattedPhone = mobileClean.length === 10 ? `91${mobileClean}` : mobileClean;
    const dueDateFormatted = item.targetDueDate
      ? formatDate(item.targetDueDate)
      : "Today";

    const msg = customTemplate
      .replace(/{customerName}/g, item.customerName)
      .replace(/{applicationNo}/g, item.applicationNo)
      .replace(/{workTypeName}/g, item.workTypeName || "RTO Work")
      .replace(/{dueDate}/g, dueDateFormatted)
      .replace(/{daysLeft}/g, String(item.daysLeft));

    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(msg)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-1">WhatsApp Expiry & Followup Reminders 💬</h4>
          <p className="text-muted mb-0">
            System-generated WhatsApp reminders for 1 Month (30 Days), 15 Days, 5 Days, and Today timelines.
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={loadReminders}>
          Refresh
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      {/* METRICS CARDS */}
      <div className="row g-3">
        <div className="col-6 col-md-4 col-xl-2">
          <div
            className={`card cursor-pointer border-2 ${
              activeInterval === "30_DAYS" ? "border-primary shadow" : ""
            }`}
            onClick={() => setActiveInterval("30_DAYS")}
          >
            <div className="card-body py-2 text-center">
              <div className="text-muted small fw-semibold">1 Month (30 Days)</div>
              <div className="fs-3 fw-bold text-primary">{summary.count30Days}</div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl-2">
          <div
            className={`card cursor-pointer border-2 ${
              activeInterval === "15_DAYS" ? "border-info shadow" : ""
            }`}
            onClick={() => setActiveInterval("15_DAYS")}
          >
            <div className="card-body py-2 text-center">
              <div className="text-muted small fw-semibold">15 Days Before</div>
              <div className="fs-3 fw-bold text-info">{summary.count15Days}</div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl-2">
          <div
            className={`card cursor-pointer border-2 ${
              activeInterval === "5_DAYS" ? "border-warning shadow" : ""
            }`}
            onClick={() => setActiveInterval("5_DAYS")}
          >
            <div className="card-body py-2 text-center">
              <div className="text-muted small fw-semibold">5 Days Before</div>
              <div className="fs-3 fw-bold text-warning">{summary.count5Days}</div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl-2">
          <div
            className={`card cursor-pointer border-2 ${
              activeInterval === "TODAY" ? "border-success shadow" : ""
            }`}
            onClick={() => setActiveInterval("TODAY")}
          >
            <div className="card-body py-2 text-center">
              <div className="text-muted small fw-semibold">Due Today</div>
              <div className="fs-3 fw-bold text-success">{summary.countToday}</div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl-2">
          <div
            className={`card cursor-pointer border-2 ${
              activeInterval === "OVERDUE" ? "border-danger shadow" : ""
            }`}
            onClick={() => setActiveInterval("OVERDUE")}
          >
            <div className="card-body py-2 text-center">
              <div className="text-muted small fw-semibold">Overdue</div>
              <div className="fs-3 fw-bold text-danger">{summary.countOverdue}</div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl-2">
          <div
            className={`card cursor-pointer border-2 ${
              activeInterval === "ALL" ? "border-dark shadow" : ""
            }`}
            onClick={() => setActiveInterval("ALL")}
          >
            <div className="card-body py-2 text-center">
              <div className="text-muted small fw-semibold">Total Upcoming</div>
              <div className="fs-3 fw-bold text-dark">{summary.totalUpcoming}</div>
            </div>
          </div>
        </div>
      </div>

      {/* TEMPLATE EDITOR PANEL */}
      <div className="card shadow-sm border-start border-success border-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h6 className="card-title mb-0 fw-bold text-success">
              📝 WhatsApp Reminder Message Template ({activeInterval.replace("_", " ")})
            </h6>
            <div className="d-flex gap-1 flex-wrap">
              <span className="small text-muted me-1">Variables:</span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm py-0 px-2 small"
                onClick={() => insertVariable("{customerName}")}
              >
                + Customer
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm py-0 px-2 small"
                onClick={() => insertVariable("{applicationNo}")}
              >
                + App No
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm py-0 px-2 small"
                onClick={() => insertVariable("{workTypeName}")}
              >
                + Work
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm py-0 px-2 small"
                onClick={() => insertVariable("{dueDate}")}
              >
                + Due Date
              </button>
            </div>
          </div>

          <SpeechInput
            as="textarea"
            name="customTemplate"
            value={customTemplate}
            onChange={(e) => setCustomTemplate(e.target.value)}
            rows={3}
          />

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="small text-muted">
              Active Target: <strong>{items.length}</strong> customer records in interval{" "}
              <strong>{activeInterval}</strong>
            </div>
            <button
              type="button"
              className="btn btn-success"
              disabled={sending || !items.length}
              onClick={handleSendBatchReminders}
            >
              {sending ? "Sending WhatsApp Reminders..." : "🚀 Send Automatic WhatsApp Reminders"}
            </button>
          </div>
        </div>
      </div>

      {/* REMINDERS TABLE LIST */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Application No</th>
                  <th>Customer Name</th>
                  <th>Mobile Number</th>
                  <th>Work Details</th>
                  <th>Target Due Date</th>
                  <th>Timeline Interval</th>
                  <th>Last Send Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      Loading WhatsApp reminders...
                    </td>
                  </tr>
                ) : null}

                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      No customer reminder records found for interval filter:{" "}
                      <strong>{activeInterval}</strong>
                    </td>
                  </tr>
                ) : null}

                {!loading &&
                  items.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.applicationNo}</td>
                      <td className="fw-bold text-uppercase">{item.customerName}</td>
                      <td>
                        <a href={`tel:${item.customerMobile}`} className="text-decoration-none">
                          📱 {item.customerMobile}
                        </a>
                      </td>
                      <td>
                        <div className="fw-semibold small">{item.workTypeName || "-"}</div>
                        <div className="text-muted small">
                          {item.workDescription || item.workName || "-"}
                        </div>
                        {item.vehicleNo ? (
                          <div className="badge text-bg-light border text-dark mt-1">
                            🚗 {item.vehicleNo}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="fw-semibold small">
                          {item.targetDueDate
                            ? formatDate(item.targetDueDate)
                            : "-"}
                        </div>
                        <div className="small text-muted">
                          {item.daysLeft > 0
                            ? `In ${item.daysLeft} days`
                            : item.daysLeft === 0
                            ? "Due Today"
                            : `${Math.abs(item.daysLeft)} days overdue`}
                        </div>
                      </td>
                      <td>
                        {item.reminderType === "30_DAYS" ? (
                          <span className="badge text-bg-primary">1 Month (30 Days)</span>
                        ) : item.reminderType === "15_DAYS" ? (
                          <span className="badge text-bg-info">15 Days Before</span>
                        ) : item.reminderType === "5_DAYS" ? (
                          <span className="badge text-bg-warning">5 Days Before</span>
                        ) : item.reminderType === "TODAY" ? (
                          <span className="badge text-bg-success">Due Today</span>
                        ) : item.reminderType === "OVERDUE" ? (
                          <span className="badge text-bg-danger">Overdue</span>
                        ) : (
                          <span className="badge text-bg-secondary">{item.reminderType}</span>
                        )}
                      </td>
                      <td>
                        {item.lastSendStatus === "SENT" ? (
                          <span className="badge text-bg-success">SENT 🟢</span>
                        ) : item.lastSendStatus === "SIMULATED" ? (
                          <span className="badge text-bg-warning text-dark">SIMULATED 🟡</span>
                        ) : item.lastSendStatus === "FAILED" ? (
                          <span className="badge text-bg-danger">FAILED 🔴</span>
                        ) : (
                          <span className="badge text-bg-secondary">NOT SENT ⚪</span>
                        )}
                        {item.lastSentAt ? (
                          <div className="small text-muted">
                            {formatDate(item.lastSentAt)}
                          </div>
                        ) : null}
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-success"
                            disabled={sending}
                            title="Send WhatsApp API Reminder"
                            onClick={() => handleSendSingleReminder(item)}
                          >
                            💬 Send API
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success"
                            title="Open Web WhatsApp Chat"
                            onClick={() => openWhatsAppWeb(item)}
                          >
                            🔗 Web Chat
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
